import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, AlertCircle, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [loginMode, setLoginMode] = useState('otp'); // 'otp' or 'password'
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  
  // OTP state
  const [phone, setPhone] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpValidity, setOtpValidity] = useState(5);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  
  // Password state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  // After a forced logout (see lib/api.js) bring the user back to the page
  // they were on instead of always landing on /ats.
  const getPostLoginPath = () => {
    try {
      const saved = sessionStorage.getItem('ats_return_to');
      if (saved && saved.startsWith('/') && !saved.startsWith('/login')) {
        sessionStorage.removeItem('ats_return_to');
        return saved;
      }
    } catch (e) {
      // storage unavailable
    }
    return '/ats';
  };
  
  // Refs for OTP inputs
  const otpRefs = useRef([]);

  // Resend timer countdown
  useEffect(() => {
    let interval;
    if (step === 'otp' && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, resendTimer]);

  const formatPhone = (phoneNum) => {
    const digits = phoneNum.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+91-${digits}`;
    } else if (digits.length > 10) {
      return `+${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    return phoneNum;
  };

  const handleRequestOtp = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setOtpLoading(true);

    try {
      const response = await authApi.requestOtp(phone);
      const data = response.data;
      
      setOtpValidity(data.otp_validity_minutes || 5);
      setStep('otp');
      setResendTimer(30);
      setCanResend(false);
      setOtpDigits(['', '', '', '', '', '']);
      
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
      
      // Show success message - OTP sent via WhatsApp
      toast.success('OTP sent to your WhatsApp!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = digit;
    setOtpDigits(newOtpDigits);
    
    if (digit && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
    
    const fullOtp = newOtpDigits.join('');
    if (fullOtp.length === 6) {
      handleVerifyOtp(fullOtp);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length > 0) {
      const newOtpDigits = [...otpDigits];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newOtpDigits[i] = pastedData[i];
      }
      setOtpDigits(newOtpDigits);
      
      const nextEmpty = pastedData.length < 6 ? pastedData.length : 5;
      otpRefs.current[nextEmpty]?.focus();
      
      if (pastedData.length === 6) {
        handleVerifyOtp(pastedData);
      }
    }
  };

  const handleVerifyOtp = async (otpValue) => {
    setError('');
    setLoading(true);

    try {
      const response = await authApi.verifyOtp(phone, otpValue);
      const { access_token, user } = response.data;
      login(user, access_token);
      toast.success(`Welcome back, ${user.full_name}!`);
      navigate(getPostLoginPath());
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP');
      setOtpDigits(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
      setLoading(false);
    }
  };

  const handleResendOtp = () => {
    if (canResend) {
      handleRequestOtp();
    }
  };

  const handleBack = () => {
    setStep('phone');
    setError('');
    setOtpDigits(['', '', '', '', '', '']);
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authApi.login(email, password);
      const { access_token, user } = response.data;
      login(user, access_token);
      toast.success(`Welcome back, ${user.full_name}!`);
      navigate(getPostLoginPath());
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // Password Login UI
  if (loginMode === 'password') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4 max-md:p-3">
        <div className="w-full max-w-sm border border-slate-200 rounded-2xl p-8 max-md:p-5">
          {/* Logo */}
          <div className="text-center mb-10 max-md:mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_talent-match-129/artifacts/10z830dt_Daruwallas%20Consultancy%20Services%20LLP%20Logo.png" 
              alt="DCS Logo" 
              className="w-20 h-20 object-contain mx-auto mb-4"
            />
            <h1 className="text-xl font-semibold text-slate-800">DCS ATS</h1>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-lg mb-4">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handlePasswordLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                className="h-12 max-md:h-[44px] max-md:text-[16px] text-slate-900 placeholder:text-slate-500 border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                className="h-12 max-md:h-[44px] max-md:text-[16px] text-slate-900 placeholder:text-slate-500 border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 max-md:h-[48px] bg-slate-900 hover:bg-slate-800 text-white font-medium"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => { setLoginMode('otp'); setError(''); }}
              className="text-sm text-slate-500 hover:text-slate-700 max-md:inline-flex max-md:items-center max-md:justify-center max-md:min-h-[44px] max-md:px-3"
              data-testid="switch-to-otp"
            >
              Login with Phone OTP instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  // OTP Login - Step 2: Enter OTP
  if (step === 'otp') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4 max-md:p-3">
        <div className="w-full max-w-sm border border-slate-200 rounded-2xl p-8 max-md:p-4">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-8 max-md:min-h-[44px] max-md:-ml-1 max-md:px-1"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>

          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_talent-match-129/artifacts/10z830dt_Daruwallas%20Consultancy%20Services%20LLP%20Logo.png" 
              alt="DCS Logo" 
              className="w-16 h-16 object-contain mx-auto mb-4"
            />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-slate-800">Verify OTP</h1>
            <p className="text-slate-500 mt-2 text-sm">
              Enter the 6-digit code sent to
            </p>
            <p className="text-slate-800 font-medium mt-1">
              {formatPhone(phone)}
            </p>
          </div>

          {error && (
            <div className="flex items-center justify-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-lg mb-6">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* OTP Input Boxes */}
          <div className="flex justify-center gap-3 mb-8 max-md:gap-1.5">
            {otpDigits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (otpRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={index === 0 ? handleOtpPaste : undefined}
                disabled={loading}
                className="w-12 h-14 text-center text-xl font-semibold text-slate-800 border-2 border-slate-200 rounded-lg focus:border-slate-900 focus:ring-0 outline-none transition-colors disabled:bg-slate-50 disabled:text-slate-400 max-md:w-auto max-md:flex-1 max-md:min-w-0 max-md:px-0"
                data-testid={`otp-digit-${index}`}
              />
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center gap-2 mb-6">
              <Loader2 className="w-5 h-5 animate-spin text-slate-600" />
              <span className="text-slate-600 text-sm">Verifying...</span>
            </div>
          )}

          {/* Resend Timer */}
          <div className="text-center mb-8">
            {canResend ? (
              <button
                onClick={handleResendOtp}
                className="text-slate-900 hover:text-slate-700 text-sm font-medium"
                disabled={otpLoading}
              >
                {otpLoading ? 'Sending...' : 'Resend OTP'}
              </button>
            ) : (
              <p className="text-slate-500 text-sm">
                Didn't receive OTP? <span className="text-slate-700 font-medium">Resend in {String(Math.floor(resendTimer / 60)).padStart(2, '0')}:{String(resendTimer % 60).padStart(2, '0')}</span>
              </p>
            )}
          </div>

          {/* Switch to Password */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => { setLoginMode('password'); setError(''); }}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Login with Email & Password instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  // OTP Login - Step 1: Enter Phone
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4 max-md:p-3">
      <div className="w-full max-w-sm border border-slate-200 rounded-2xl p-8 max-md:p-5">
        {/* Logo */}
        <div className="text-center mb-10 max-md:mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_talent-match-129/artifacts/10z830dt_Daruwallas%20Consultancy%20Services%20LLP%20Logo.png" 
            alt="DCS Logo" 
            className="w-20 h-20 object-contain mx-auto mb-4"
          />
          <h1 className="text-xl font-semibold text-slate-800">DCS ATS</h1>
          <p className="text-slate-500 text-sm mt-1">AI-Powered Talent Management</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-lg mb-4">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleRequestOtp} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Mobile Number</label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter your mobile number"
              required
              inputMode="tel"
              autoComplete="tel"
              className="h-12 max-md:h-[44px] max-md:text-[16px] text-slate-900 placeholder:text-slate-500 border-slate-200 focus:border-slate-400 focus:ring-slate-400"
              data-testid="login-phone"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12 max-md:h-[48px] bg-slate-900 hover:bg-slate-800 text-white font-medium"
            disabled={otpLoading || !phone.trim()}
            data-testid="request-otp-btn"
          >
            {otpLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Get OTP'}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => { setLoginMode('password'); setError(''); }}
            className="text-sm text-slate-500 hover:text-slate-700 max-md:inline-flex max-md:items-center max-md:justify-center max-md:min-h-[44px] max-md:px-3"
            data-testid="switch-to-password"
          >
            Login with Email & Password instead
          </button>
        </div>
      </div>
    </div>
  );
}
